import {
    CheckOutlined,
    ExclamationCircleFilled,
    CalendarOutlined,
    BankOutlined,
    WarningOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    PrinterOutlined,
    CheckCircleOutlined,
    MinusCircleOutlined,
    DollarOutlined,
    ReloadOutlined,
    EyeOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Modal,
    Select,
    Skeleton,
    Tabs,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Badge,
    Divider,
    Switch,
    Tag,
    Tooltip,
    Alert
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useMemo, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { frenchMonth } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import RetrocessionStyle from './RetrocessionStyle';
import { annulationsColumns, cessionParCompagnieColumns, emissionsColumns, recoursColumns, sinistresColumns } from './RetrocessionData';
import { BordereauParCompagnie } from './BordereauParCompagnie';
import {
    calculerRecapitulatifGlobal,
    grouperCessionsParCompagnie,
    traiterAnnulationsCompagnie,
    traiterEmissionsCompagnie,
    traiterRecoursCompagnie,
    traiterSinistresCompagnie,
    indexerExclusions,
    lookupExclusion,
} from './retrocessionHooks';
import MotifExclusionModal from './components/MotifExclusionModal';
import TableauRecapitulatifRetrocession from './components/TableauRecapitulatifRetrocession';
import VtRetrocession from './components/VtRetrocession';
import { construireMapDecomposition, appliquerDecomposition, filtrerLignesDansCession } from './primeNetteCession';

const { confirm } = Modal;

// Reducer pour gérer l'état des validations
const validationReducer = (state, action) => {
    switch (action.type) {
        case 'VALIDATE_EMISSIONS':
            return { ...state, emissions: { validated: true, data: action.payload } };
        case 'VALIDATE_ANNULATIONS':
            return { ...state, annulations: { validated: true, data: action.payload } };
        case 'VALIDATE_SINISTRES':
            return { ...state, sinistresPayes: { validated: true, data: action.payload } };
        case 'VALIDATE_RECOURS':
            return { ...state, recoursEncaisses: { validated: true, data: action.payload } };
        case 'VALIDATE_RECAP':
            return { ...state, recapValidated: true };
        case 'RESET':
            return initialValidationState;
        default:
            return state;
    }
};

const initialValidationState = {
    emissions: { validated: false, data: [] },
    annulations: { validated: false, data: [] },
    sinistresPayes: { validated: false, data: [] },
    recoursEncaisses: { validated: false, data: [] },
    recapValidated: false
};

const Retrocession = () => {
    // États pour les filtres
    const [categories, setCategories] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [moisRetrocession, setMoisRetrocession] = useState(null);
    const [dateLimiteCession, setDateLimiteCession] = useState(null);
    // SUPPRIMÉ : clesRepartition n'est plus nécessaire
    const [penalites, setPenalites] = useState([]);

    // États pour les données de cession par compagnie
    const [cessionsParCompagnie, setCessionsParCompagnie] = useState([]);
    const [retrocessionExistante, setRetrocessionExistante] = useState(null);
    const [loadingRetrocession, setLoadingRetrocession] = useState(false);
    const [recalculing, setRecalculing] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    // Aperçu du récapitulatif avant validation (POST /preview, sans persistance).
    const [apercuVisible, setApercuVisible] = useState(false);
    const [apercuGroup, setApercuGroup] = useState(null);
    const [loadingApercu, setLoadingApercu] = useState(false);
    // État de correction (backend) par cession anomale : cessionId -> { corrige, figee, ... }.
    const [correctionsCession, setCorrectionsCession] = useState({});
    // Anomalies détectées côté BACKEND (autoritaire) : [{ cessionId, compagnie, count, total }].
    const [anomaliesCession, setAnomaliesCession] = useState([]);

    // État de validation
    const [validationState, dispatch] = useReducer(validationReducer, initialValidationState);
    const [tabId, setTabId] = useState("1");

    // États pour les Collapse
    const [activeEmissionsKeys, setActiveEmissionsKeys] = useState([]);
    const [activeAnnulationsKeys, setActiveAnnulationsKeys] = useState([]);
    const [activeSinistresKeys, setActiveSinistresKeys] = useState([]);
    const [activeRecoursKeys, setActiveRecoursKeys] = useState([]);

    // ── Lignes écartées (comptes de cession corrigés) ──
    const [exclusions, setExclusions] = useState([]);
    const exclusionIndex = useMemo(() => indexerExclusions(exclusions), [exclusions]);
    // Vue affichée : « corrigée » (par défaut s'il existe des exclusions) ou « compagnie » (brute).
    const [vueCorrigee, setVueCorrigee] = useState(true);
    const [motifModal, setMotifModal] = useState({ open: false, context: null });

    const chargerExclusions = () => {
        if (!moisRetrocession) { setExclusions([]); return; }
        axiosInstance.get(`/api/retrocession/exclusions`, { params: { mois: moisRetrocession } })
            .then((res) => setExclusions(res.data || []))
            .catch(() => setExclusions([]));
    };
    useEffect(() => { chargerExclusions(); /* eslint-disable-next-line */ }, [moisRetrocession]);

    const onExclureLigne = (type, record) => setMotifModal({
        open: true,
        context: { type, ligne: record, compagnieCode: record?.compagnie?.code || record?.compagnieCedante?.code, mois: moisRetrocession },
    });
    const onRetablirLigne = (record, exclusion) => {
        axiosInstance.delete(`/api/retrocession/exclusion/${exclusion.codeId}`)
            .then(() => { toast.success("Ligne rétablie dans la rétrocession."); chargerExclusions(); })
            .catch(() => toast.error("Échec du rétablissement."));
    };
    // Opts d'action par type (Écarter / Rétablir + lookup).
    const exclusionOptsFor = (type) => ({
        onExclure: (record) => onExclureLigne(type, record),
        onRetablir: onRetablirLigne,
        lookup: (record) => lookupExclusion(exclusionIndex, type, record),
    });
    // Filtre « version corrigée » d'AFFICHAGE : suit le toggle (compagnie = tout ; corrigée = sans écartées).
    const filtrerCorrige = (type, lignes) =>
        vueCorrigee ? (lignes || []).filter((l) => !lookupExclusion(exclusionIndex, type, l)) : (lignes || []);
    // Filtre OFFICIEL (validation / génération) : TOUJOURS sans les lignes écartées, quel que soit le toggle.
    const retirerExclues = (type, lignes) =>
        (lignes || []).filter((l) => !lookupExclusion(exclusionIndex, type, l));

    useEffect(() => {
        Promise.all([
            axiosInstance.get(`/api/categorie`),
            axiosInstance.get(`/api/branche`),
            axiosInstance.get(`/api/organisme/assurance`)
        ])
            .then(([catRes, brancheRes, compRes]) => {
                const excludedCodes = ["A", "B", "T"];
                const filtered = catRes.data.filter(
                    (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
                );

                const hasTPVCategories = catRes.data.some((c) => excludedCodes.includes(c.codeCategorie));
                if (hasTPVCategories) {
                    filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
                }

                setCategories(filtered);
                setBranches(brancheRes.data);
                setCompagnies(compRes.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
                setLoading(false);
            });

        chargerParametresPenalites();
    }, []);

    useEffect(() => {
        // VT : modèle SPÉCIFIQUE (production VT + charges), géré par le composant dédié <VtRetrocession>.
        // On NE charge PAS les cessions (le flux cession classique ne s'applique pas au VT).
        if (selectedCategorie && selectedCategorie !== 'VT' && moisRetrocession) {
            // SUPPRIMÉ : chargerClesRepartition() n'est plus nécessaire
            chargerCessionsValidees();
            verifierRetrocessionExistante();
        }
    }, [selectedCategorie, moisRetrocession]);

    const chargerParametresPenalites = () => {
        axiosInstance
            .get('/api/parametre-penalite')
            .then((res) => setPenalites(res.data))
            .catch((err) => {
                console.error(err);
                toast.warning("Paramètres de pénalités non configurés");
            });
    };

    // SUPPRIMÉ : chargerClesRepartition() - plus nécessaire

    const chargerCessionsValidees = () => {
        setLoading(true);
        axiosInstance
            .get('/api/cession/validees-avec-details-et-categorie', {
                params: {
                    mois: moisRetrocession,
                    categorieCode: selectedCategorie
                }
            })
            .then(async (res) => {
                const cessions = res.data || [];
                // ASSIETTE PRIME NETTE : les bordereaux agrégés ne portent que le TTC (primeACeder).
                // On injecte la décomposition (PN Pool/Compagnie, accessoires, taxes) par règlement,
                // comme /toutes-les-cessions, pour que la rétrocession s'assoie sur la prime nette.
                const moisParam = `${moisRetrocession}-01`;
                await Promise.all(cessions.map(async (cession) => {
                    // Émissions BRUTES (avant filtrage retro) : la ligne sur contrat SANS date d'effet/
                    // échéance est ÉCARTÉE par filtrerLignesDansCession (absente de la décompo retro) —
                    // on la conserve ici pour détecter l'anomalie et proposer la correction du compte.
                    cession._emissionsBrutes = [...(cession?.bordereauEmissions?.items || [])];
                    const code = cession?.compagnie?.code;
                    if (!code) return;
                    try {
                        const decompoRes = await axiosInstance.get(
                            `/api/contrat/organisme/month/cession/${code}`,
                            { timeout: 120000, params: { codeCategorie: selectedCategorie, mois: moisParam } }
                        );
                        const map = construireMapDecomposition(decompoRes.data);
                        // 1) N'garder QUE les lignes réellement dans la cession (mêmes lignes que le
                        //    compte de cession) → écarte les règlements non rattachés captés par la 3ᵉ
                        //    branche de get_cessions_for_retro_wr (sinon rétro ≠ cession).
                        // 2) Puis injecter la décomposition prime nette sur les lignes conservées.
                        if (cession?.bordereauEmissions) cession.bordereauEmissions.items = appliquerDecomposition(filtrerLignesDansCession(cession.bordereauEmissions.items, map), map);
                        if (cession?.bordereauAnnulations) cession.bordereauAnnulations.items = appliquerDecomposition(filtrerLignesDansCession(cession.bordereauAnnulations.items, map), map);
                    } catch (e) {
                        console.error("Décomposition prime nette indisponible (cession " + code + ") :", e?.message);
                    }
                }));
                // TAUX de répartition de l'exercice = repli pour recomposer les lignes dont la répartition
                // STOCKÉE est vide (reprises). ⚠️ get_all_taux_repartition attend une CATÉGORIE ('M'), pas
                // un code BRANCHE ('MOTO') → on prend la catégorie des lignes (item.categorie), pas
                // `selectedCategorie` (qui porte la branche). Sinon 'MOTO' → 0 taux → lignes vides à 0 %.
                let tauxRepartition = null;
                let tauxParExerciceSurvenance = {};
                try {
                    const catRepr = cessions.flatMap((c) => [
                            ...(c?.bordereauEmissions?.items || []),
                            ...(c?.bordereauAnnulations?.items || []),
                        ]).map((it) => it?.categorie).find(Boolean)
                        || selectedCategorie;
                    const tauxRes = await axiosInstance.get('/api/retrocession/taux', {
                        params: { date: moisParam, categorie: catRepr },
                    });
                    tauxRepartition = Array.isArray(tauxRes.data) && tauxRes.data.length ? tauxRes.data : null;

                    // SINISTRES/RECOURS : le taux dépend de l'EXERCICE DE SURVENANCE de chaque ligne
                    // (date_sinistre), pas de l'exercice de cession → il n'est pas uniforme. On récupère
                    // les clés une seule fois par année de survenance distincte (get_all_taux_repartition
                    // résout l'exercice depuis la date passée), puis on indexe par année.
                    const anneesSurvenance = [...new Set(
                        cessions.flatMap((c) => [
                            ...(c?.bordereauSinistresPayes?.items || []),
                            ...(c?.bordereauRecoursEncaisses?.items || []),
                        ]).map((it) => String(it?.dateSinistre || "").slice(0, 4))
                          .filter((y) => /^\d{4}$/.test(y))
                    )];
                    const paires = await Promise.all(anneesSurvenance.map(async (annee) => {
                        try {
                            const r = await axiosInstance.get('/api/retrocession/taux', {
                                params: { date: `${annee}-01-01`, categorie: catRepr },
                            });
                            return [annee, Array.isArray(r.data) && r.data.length ? r.data : null];
                        } catch (_) {
                            return [annee, null];
                        }
                    }));
                    tauxParExerciceSurvenance = Object.fromEntries(paires.filter(([, v]) => v));
                } catch (e) {
                    console.error("Taux de répartition indisponibles :", e?.message);
                }
                const cessionsGroupees = grouperCessionsParCompagnie(cessions, dateLimiteCession, penalites, moisRetrocession, tauxRepartition, tauxParExerciceSurvenance);
                // Ordre des lignes « Suivi des cessions par compagnie » = ordre du CODE (tri naturel : 01, 02, 03…).
                cessionsGroupees.sort((a, b) =>
                    String(a.compagnie?.code || "").localeCompare(String(b.compagnie?.code || ""), undefined, { numeric: true }));
                setCessionsParCompagnie(cessionsGroupees);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des cessions");
                setLoading(false);
            });
    };

    // Recalcule EN PLACE les rétrocessions déjà stockées du mois : réassoit sur la PRIME NETTE
    // + arrondi au franc (Σ = base). Corrige les règlements encaissés avant le changement d'assiette.
    const recalculerRetrocessions = () => {
        if (!moisRetrocession) {
            toast.warning("Veuillez d'abord sélectionner le mois de rétrocession");
            return;
        }
        Modal.confirm({
            title: "Recalculer les rétrocessions ?",
            content: `Les répartitions de rétrocession des ÉMISSIONS (prime nette) ET des SINISTRES PAYÉS (montant payé, clé de l'exercice de survenance) de ${frenchMonth(moisRetrocession.split("-")[1])} ${moisRetrocession.split("-")[0]} seront recalculées (arrondi au franc). Cette action réécrit les données stockées.`,
            okText: "Recalculer",
            cancelText: "Annuler",
            onOk: () => {
                setRecalculing(true);
                return axiosInstance
                    .post('/api/retrocession/recalculer', {
                        mois: moisRetrocession,
                        // null = toutes les compagnies du mois (le mois est le périmètre naturel de la rétro).
                        compagnie: null
                    })
                    .then((res) => {
                        const r = res.data || {};
                        toast.success(`Rétrocessions recalculées : ${r.recalcules ?? 0} ligne(s) mise(s) à jour (émissions + sinistres payés)`);
                        chargerCessionsValidees();
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error("Erreur lors du recalcul des rétrocessions");
                    })
                    .finally(() => setRecalculing(false));
            }
        });
    };

    // B3 — RÉGÉNÉRATION GUIDÉE : réinitialise la rétrocession du (mois × branche) — soft-delete gardé
    // (BLOQUÉ si déjà réglée) + rafraîchissement des comptes de cession depuis la décompo corrigée —
    // puis déverrouille l'écran pour reconstruire via « Valider ».
    const regenererRetrocession = () => {
        if (!moisRetrocession || !selectedCategorie) {
            toast.warning("Sélectionnez d'abord le mois et la branche");
            return;
        }
        Modal.confirm({
            title: "Régénérer la rétrocession ?",
            content: `La rétrocession de ${frenchMonth(moisRetrocession.split("-")[1])} ${moisRetrocession.split("-")[0]} (branche ${selectedCategorie}) va être RÉINITIALISÉE : les snapshots existants seront désactivés et les comptes de cession rafraîchis depuis les données corrigées. Action BLOQUÉE si une rétrocession est déjà réglée (payée / chèque émis / accusé de réception). Vous devrez ensuite cliquer sur « Valider » pour la reconstruire.`,
            okText: "Régénérer",
            okButtonProps: { danger: true },
            cancelText: "Annuler",
            onOk: () => {
                setRegenerating(true);
                return axiosInstance
                    .post('/api/retrocession/regenerer-preparer', {
                        mois: moisRetrocession + "-01",
                        branche: selectedCategorie
                    })
                    .then((res) => {
                        const r = res.data || {};
                        toast.success(`Rétrocession réinitialisée : ${r.retrosDesactivees ?? 0} désactivée(s), ${r.cessionsRafraichies ?? 0} cession(s) rafraîchie(s). Cliquez sur « Valider » pour reconstruire.`);
                        dispatch({ type: 'RESET' });          // ré-ouvre les étapes de validation
                        setRetrocessionExistante(false);      // lève le verrou écran (backend a soft-delete)
                        chargerCessionsValidees();            // recharge les cessions corrigées
                    })
                    .catch((err) => {
                        // 409 = garde « déjà réglée » (include-message=never → on mappe par statut).
                        if (err?.response?.status === 409) {
                            toast.error("Régénération bloquée : une rétrocession de ce mois/branche est déjà réglée (payée, chèque émis ou accusé de réception). Régularisation manuelle avec le Pool.");
                        } else {
                            toast.error("Erreur lors de la régénération de la rétrocession");
                        }
                    })
                    .finally(() => setRegenerating(false));
            }
        });
    };

    const verifierRetrocessionExistante = () => {
        axiosInstance
            .get('/api/retrocession/verifier', {
                params: {
                    mois: moisRetrocession + "-01",
                    categorie: selectedCategorie
                }
            })
            .then((res) => {
                if (res.data) {
                    setRetrocessionExistante(res.data);
                    dispatch({ type: 'VALIDATE_RECAP' });
                }
            })
            .catch((err) => console.error(err));
    };

    // =========================================================================
    // Fonctions de validation (simplifiées : plus de clesRepartition)
    // =========================================================================

    const confirmValidationEmissions = () => {
        confirm({
            title: "Validation du bordereau des émissions rétrocédées",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les émissions avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const emissionsGlobales = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
                    emissionsGlobales.push(...retirerExclues("EMISSION", emissionsRetrocedees));
                });
                dispatch({ type: 'VALIDATE_EMISSIONS', payload: emissionsGlobales });
                toast.success("Bordereau des émissions validé");
            },
        });
    };

    const confirmValidationAnnulations = () => {
        confirm({
            title: "Validation du bordereau des annulations rétrocédées",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les annulations avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const annulationsGlobales = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
                    annulationsGlobales.push(...retirerExclues("ANNULATION", annulationsRetrocedees));
                });
                dispatch({ type: 'VALIDATE_ANNULATIONS', payload: annulationsGlobales });
                toast.success("Bordereau des annulations validé");
            },
        });
    };

    const confirmValidationSinistres = () => {
        confirm({
            title: "Validation du bordereau des sinistres payés rétrocédés",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les sinistres avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const sinistresGlobaux = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
                    sinistresGlobaux.push(...retirerExclues("SINISTRE", sinistresRetrocedes));
                });
                dispatch({ type: 'VALIDATE_SINISTRES', payload: sinistresGlobaux });
                toast.success("Bordereau des sinistres validé");
            },
        });
    };

    const confirmValidationRecours = () => {
        confirm({
            title: "Validation du bordereau des recours encaissés rétrocédés",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les recours avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const recoursGlobaux = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);
                    recoursGlobaux.push(...retirerExclues("RECOURS", recoursRetrocedes));
                });
                dispatch({ type: 'VALIDATE_RECOURS', payload: recoursGlobaux });
                toast.success("Bordereau des recours validé");
            },
        });
    };

    const confirmValidationRecapitulatif = () => {
        confirm({
            title: "Validation du récapitulatif global",
            icon: <ExclamationCircleFilled />,
            content: "Cette action clôturera la rétrocession pour cette période. Confirmez-vous ?",
            okText: "Valider et clôturer",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                validerRetrocession();
            },
        });
    };

    // Construit le payload de rétrocession — PARTAGÉ entre la validation et l'aperçu (mêmes chiffres).
    const construireRetrocessionData = () => {
        // Récap + payload OFFICIELS : TOUJOURS sur la base corrigée (lignes écartées retirées).
        const recap = calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, exclusionIndex);

        // 1. Collecter toutes les données traitées depuis toutes les cédantes (sans les lignes écartées)
        const allEmissions = [];
        const allEmissionsReportees = [];
        const allAnnulations = [];
        const allSinistres = [];
        const allRecours = [];

        cessionsParCompagnie.forEach(compagnie => {
            const { emissionsRetrocedees, emissionsReportees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
            allEmissions.push(...retirerExclues("EMISSION", emissionsRetrocedees));
            allEmissionsReportees.push(...emissionsReportees);
            allAnnulations.push(...retirerExclues("ANNULATION", traiterAnnulationsCompagnie(compagnie, moisRetrocession)));
            allSinistres.push(...retirerExclues("SINISTRE", traiterSinistresCompagnie(compagnie, moisRetrocession)));
            allRecours.push(...retirerExclues("RECOURS", traiterRecoursCompagnie(compagnie, moisRetrocession)));
        });

        // 2. Calculer les totaux par compagnie BÉNÉFICIAIRE (identique à l'UI)
        const retrocessionParCompagnie = compagniesRepartition.map(comp => {
            const totalEmissions = allEmissions.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalAnnulations = allAnnulations.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalSinistres = allSinistres.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalRecours = allRecours.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            // Pénalité : chercher la cédante correspondante (si cette bénéficiaire est aussi cédante)
            const cedante = cessionsParCompagnie.find(c => c.compagnie.code === comp.code);

            // PRIME REPORTÉE de CETTE compagnie EN TANT QUE CÉDANTE (Σ prime nette des émissions
            // reportées taguées par cédante) → compte de cession : vrai cédé = total cédé − reportée.
            const totalEmissionsReportees = allEmissionsReportees
                .filter((item) => item?.compagnie?.code === comp.code)
                .reduce((sum, item) => sum + (Number(item?.montantOriginal) || 0), 0);

            return {
                compagnie: comp,
                emissions: allEmissions,
                emissionsReportees: allEmissionsReportees,
                annulations: allAnnulations,
                sinistres: allSinistres,
                recours: allRecours,
                penalite: cedante?.penalite || 0,
                joursRetard: cedante?.joursRetard || 0,
                totalEmissions,
                totalEmissionsReportees,
                totalAnnulations,
                totalSinistres,
                totalRecours,
            };
        });

        return {
            utilisateur: LOGGED_USER.codeId,
            moisRetrocession: moisRetrocession + "-01",
            dateLimiteCession,
            branche: branches.find(c => c.codeBranche === selectedCategorie),
            retrocessionParCompagnie,
            recapitulatifGlobal: recap
        };
    };

    const validerRetrocession = () => {
        setLoadingRetrocession(true);
        const retrocessionData = construireRetrocessionData();
        axiosInstance
            .post('/api/retrocession', retrocessionData)
            .then((res) => {
                dispatch({ type: 'VALIDATE_RECAP' });
                toast.success("Rétrocession validée et clôturée avec succès!");
                setLoadingRetrocession(false);
                setRetrocessionExistante(res.data);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors de la validation de la rétrocession");
                setLoadingRetrocession(false);
            });
    };

    // APERÇU (sans persistance) : POST /preview → modal avec le récap détaillé = EXACTEMENT ce qui sera validé.
    const previsualiserRecap = () => {
        setLoadingApercu(true);
        const retrocessionData = construireRetrocessionData();
        // Lignes brutes (émissions + sinistres) — présentes sur chaque bénéficiaire (liste identique) :
        // servent au 2ᵉ onglet « Par exercice » (ventilation par exercice, non renvoyée par /preview
        // qui ne porte que les totaux agrégés).
        const rpc0 = retrocessionData.retrocessionParCompagnie?.[0];
        axiosInstance
            .post('/api/retrocession/preview', retrocessionData)
            .then((res) => {
                setApercuGroup({
                    retrocessions: res.data || [],
                    emissionsLignes: rpc0?.emissions || [],
                    sinistresLignes: rpc0?.sinistres || [],
                    branche: branches.find(c => c.codeBranche === selectedCategorie),
                    moisRapport: moisRetrocession + "-01",
                });
                setApercuVisible(true);
                setLoadingApercu(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors de la génération de l'aperçu");
                setLoadingApercu(false);
            });
    };

    // Habilitation HOP112 : corriger un compte de cession pour anomalie (contrat sans date).
    const hasHOP112 = (LOGGED_USER?.profil?.habilitations || []).some((h) => h?.codeHabilitation === "HOP112");

    // Corrige le compte de cession (écarte les lignes anomales) — HOP112. Recharge ensuite la rétro.
    const corrigerCessionAnomalie = (a) => {
        Modal.confirm({
            title: "Corriger le compte de cession ?",
            content: `${a.count} ligne(s) anomale(s) (contrat sans date d'effet/échéance) — ~${displayNumbers(Math.round(a.total))} F — seront écartées du compte de cession de ${a.compagnie?.raisonSociale}. L'original (version compagnie) est conservé. ⚠️ Cette correction impacte la différence de solde de cession de la rétrocession.`,
            okText: "Corriger",
            cancelText: "Annuler",
            okButtonProps: { danger: true },
            onOk: () => axiosInstance
                .put(`/api/cession/${a.cessionId}/corriger-anomalie`)
                .then(() => {
                    toast.success("Compte de cession corrigé (version Pool)");
                    chargerCessionsValidees();
                })
                .catch((err) => {
                    console.error(err);
                    toast.error(err?.response?.data?.message || "Erreur lors de la correction du compte de cession");
                }),
        });
    };

    // Annule la correction Pool (HOP112) → rétablit la version compagnie (tant que la rétro n'est pas validée).
    const annulerCorrectionCession = (a) => {
        Modal.confirm({
            title: "Annuler la correction ?",
            content: `Le compte de cession de ${a.compagnie?.raisonSociale} reviendra à la version compagnie (originale). Possible uniquement tant que la rétrocession n'est pas validée.`,
            okText: "Annuler la correction",
            cancelText: "Fermer",
            onOk: () => axiosInstance
                .put(`/api/cession/${a.cessionId}/annuler-correction`)
                .then(() => {
                    toast.success("Correction annulée — version compagnie rétablie");
                    chargerCessionsValidees();
                })
                .catch((err) => {
                    console.error(err);
                    toast.error(err?.response?.data?.message || "Erreur lors de l'annulation de la correction");
                }),
        });
    };

    // Détection d'anomalies AUTORITAIRE (backend) + état de correction. On interroge, par cession,
    // /api/cession/{codeId}/lignes-anomales (teste directement dateEffet/dateEcheance null sur les
    // contrats et renvoie la PN Pool exacte) — fiable là où la détection frontend échouait (ligne
    // écartée de la décompo retro, `effet` pollué par la date de règlement).
    useEffect(() => {
        const cessions = [];
        (cessionsParCompagnie || []).forEach((g) =>
            (g?.cessions || []).forEach((c) => {
                if (c?.codeId) cessions.push({ codeId: c.codeId, compagnie: g.compagnie });
            }));
        if (!cessions.length) {
            setAnomaliesCession([]);
            setCorrectionsCession({});
            return;
        }
        let annule = false;
        Promise.all(cessions.map((c) =>
            axiosInstance.get(`/api/cession/${c.codeId}/lignes-anomales`)
                .then((r) => ({ ...c, lignes: Array.isArray(r.data) ? r.data : [] }))
                .catch(() => ({ ...c, lignes: [] }))
        )).then(async (results) => {
            if (annule) return;
            const anomalies = results
                .filter((r) => r.lignes.length > 0)
                .map((r) => ({
                    cessionId: r.codeId,
                    compagnie: r.compagnie,
                    count: r.lignes.length,
                    total: r.lignes.reduce((s, l) => s + (Number(l.primeNettePool) || 0), 0),
                }));
            setAnomaliesCession(anomalies);
            if (!anomalies.length) {
                setCorrectionsCession({});
                return;
            }
            const entries = await Promise.all(anomalies.map((a) =>
                axiosInstance.get(`/api/cession/${a.cessionId}/correction`)
                    .then((r) => [a.cessionId, r.data])
                    .catch(() => [a.cessionId, null])
            ));
            if (annule) return;
            const map = {};
            entries.forEach(([cid, data]) => { if (data) map[cid] = data; });
            setCorrectionsCession(map);
        });
        return () => { annule = true; };
    }, [cessionsParCompagnie]);

    const onTabsChange = (key) => {
        const allBordereauxValidated =
            validationState.emissions.validated &&
            validationState.annulations.validated &&
            validationState.sinistresPayes.validated &&
            validationState.recoursEncaisses.validated;

        if (key === "5" && !allBordereauxValidated && !retrocessionExistante) {
            toast.warning("Veuillez valider tous les bordereaux avant d'accéder au récapitulatif");
            return;
        }
        setTabId(key);
    };

    const validerDateLimite = (date) => {
        if (!moisRetrocession) {
            toast.warning("Veuillez d'abord sélectionner le mois de rétrocession");
            return false;
        }

        const finMois = dayjs(moisRetrocession).endOf('month');
        const dateLimite = dayjs(date);

        if (!dateLimite.isAfter(finMois)) {
            toast.error("La date limite doit être strictement postérieure à la fin du mois sélectionné");
            return false;
        }

        return true;
    };

    const isRetrocessionValidee = validationState.recapValidated || retrocessionExistante;

    // Extraire la liste unique des compagnies depuis les données de répartition
    const getCompagniesFromRepartition = () => {
        const compagniesSet = new Map();

        cessionsParCompagnie.forEach(compagnie => {
            // Parcourir tous les bordereaux pour extraire les compagnies
            const allItems = [
                ...(compagnie.emissions || []),
                ...(compagnie.annulations || []),
                ...(compagnie.sinistres || []),
                ...(compagnie.recours || [])
            ];

            allItems.forEach(item => {
                item.repartition?.forEach(rep => {
                    if (!compagniesSet.has(rep.compagnieCode)) {
                        compagniesSet.set(rep.compagnieCode, {
                            code: rep.compagnieCode,
                            raisonSociale: rep.compagnieNom
                        });
                    }
                });
            });
        });

        // Ordre des colonnes = ordre du CODE de la compagnie (tri naturel : 01, 03, 05…).
        return Array.from(compagniesSet.values()).sort((a, b) =>
            String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true })
        );
    };

    const compagniesRepartition = getCompagniesFromRepartition();

    return (
        <div className="layout-wrapper layout-content-navbar">
            <RetrocessionStyle />

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: 'var(--retro-bg)' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* Header */}
                            <div className="retrocession-page-header">
                                <h1>🔄 Rétrocession</h1>
                                <p>
                                    Gestion de la rétrocession mensuelle avec répartitions pré-calculées et calcul des pénalités de retard
                                </p>
                            </div>

                            {/* Filtres */}
                            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <CalendarOutlined />
                                                Mois de rétrocession
                                            </div>
                                            <DatePicker
                                                placeholder="Sélectionner"
                                                onChange={(_, dateString) => setMoisRetrocession(dateString)}
                                                picker="month"
                                                disabled={isRetrocessionValidee}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <ClockCircleOutlined />
                                                Date limite cession
                                            </div>
                                            <DatePicker
                                                placeholder="Date limite"
                                                onChange={(date, dateString) => {
                                                    if (validerDateLimite(date)) {
                                                        setDateLimiteCession(dateString);
                                                    }
                                                }}
                                                disabled={!moisRetrocession || isRetrocessionValidee}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <FileTextOutlined />
                                                Branche
                                            </div>
                                            <Select
                                                placeholder="Sélectionner"
                                                style={{ width: "100%" }}
                                                onChange={setSelectedCategorie}
                                                disabled={isRetrocessionValidee}
                                                size="large"
                                                options={branches.map((br) => ({
                                                    value: br.codeBranche,
                                                    label: br.nom,
                                                }))}
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <ReloadOutlined />
                                                Actions
                                            </div>
                                            <Button
                                                type="primary"
                                                icon={<ReloadOutlined />}
                                                onClick={chargerCessionsValidees}
                                                disabled={!selectedCategorie || !moisRetrocession}
                                                style={{
                                                    width: '100%',
                                                    height: 40,
                                                    background: 'var(--retro-primary)'
                                                }}
                                                size="large"
                                            >
                                                Actualiser
                                            </Button>
                                            <Button
                                                onClick={recalculerRetrocessions}
                                                loading={recalculing}
                                                disabled={!moisRetrocession}
                                                style={{ width: '100%', height: 40, marginTop: 8 }}
                                                size="large"
                                            >
                                                Recalculer les rétrocessions
                                            </Button>
                                            <Button
                                                danger
                                                onClick={regenererRetrocession}
                                                loading={regenerating}
                                                disabled={!moisRetrocession || !selectedCategorie}
                                                style={{ width: '100%', height: 40, marginTop: 8 }}
                                                size="large"
                                            >
                                                Régénérer la rétrocession
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Tableau de suivi */}
                            {cessionsParCompagnie.length > 0 && (
                                <Card
                                    title={
                                        <Space>
                                            <BankOutlined />
                                            <span>Suivi des cessions par compagnie</span>
                                            <Badge count={cessionsParCompagnie.length} style={{ backgroundColor: 'var(--retro-primary)' }} />
                                        </Space>
                                    }
                                    className="tracking-card"
                                >
                                    <Table
                                        columns={cessionParCompagnieColumns(dateLimiteCession)}
                                        dataSource={cessionsParCompagnie}
                                        rowKey={(record) => record.compagnie.code}
                                        pagination={false}
                                        size="middle"
                                    />
                                </Card>
                            )}

                            {/* ⚠️ Anomalie : lignes de cession sur contrat SANS date d'effet/échéance
                                (non cessible/rétrocédable). Le Pool (HOP112) peut corriger le compte de
                                cession avant de valider la rétrocession — l'original compagnie est conservé. */}
                            {anomaliesCession.length > 0 && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    message="Lignes de cession anomales (contrat sans date d'effet/échéance)"
                                    description={
                                        <div>
                                            <div style={{ marginBottom: 8 }}>
                                                Ces lignes ne sont pas rétrocédables. Le Pool (HOP112) peut corriger le
                                                compte de cession pour l'aligner sur la rétrocession — la version compagnie
                                                (originale) reste conservée.
                                            </div>
                                            {anomaliesCession.map((a) => {
                                                const etat = correctionsCession[a.cessionId];
                                                const corrige = etat?.corrige;
                                                const figee = etat?.figee;
                                                return (
                                                    <div key={a.cessionId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                                                        <span>
                                                            <b>{a.compagnie?.raisonSociale}</b> — {a.count} ligne(s), ~{displayNumbers(Math.round(a.total))} F
                                                            {corrige
                                                                ? <Tag color="green" style={{ marginLeft: 8 }}>Corrigé (Pool)</Tag>
                                                                : <Tag color="orange" style={{ marginLeft: 8 }}>À corriger</Tag>}
                                                        </span>
                                                        {corrige ? (
                                                            <Tooltip title={figee ? "Rétrocession validée : correction figée" : ""}>
                                                                <Button size="small" onClick={() => annulerCorrectionCession(a)} disabled={!hasHOP112 || figee}>
                                                                    Annuler la correction
                                                                </Button>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title={!hasHOP112 ? "Habilitation HOP112 requise" : (figee ? "Rétrocession validée : correction figée" : "")}>
                                                                <Button size="small" danger onClick={() => corrigerCessionAnomalie(a)} disabled={!hasHOP112 || figee}>
                                                                    Corriger le compte de cession
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    }
                                />
                            )}

                            {/* Rétrocession VT : modèle SPÉCIFIQUE (production VT + charges) → écran dédié */}
                            {selectedCategorie === 'VT' && (
                                <VtRetrocession mois={moisRetrocession} />
                            )}

                            {/* Contenu principal — branches classiques (basé cessions) */}
                            {selectedCategorie !== 'VT' && (
                            <div className="main-card">
                                <div className="main-card-header">
                                    <h2>
                                        Rétrocession - {selectedCategorie || "N/A"} - {moisRetrocession ? frenchMonth(moisRetrocession.split("-")[1]) + " " + moisRetrocession.split("-")[0] : "N/A"}
                                    </h2>
                                    {isRetrocessionValidee && (
                                        <span className="status-badge">
                                            <CheckOutlined />
                                            Rétrocession clôturée
                                        </span>
                                    )}
                                </div>

                                <div className="card-body" style={{ padding: 24 }}>
                                    <ConfigProvider locale={frFR}>
                                        <Skeleton loading={loading} active>
                                            {/* Comptes de cession corrigés : bascule version compagnie ↔ corrigée + résumé des écartées */}
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "10px 14px", marginBottom: 12, background: exclusions.length ? "#fff7e6" : "#f6f7fb", border: `1px solid ${exclusions.length ? "#ffd591" : "#e5e7eb"}`, borderRadius: 10 }}>
                                                <Space wrap>
                                                    <Tag color={vueCorrigee ? "green" : "blue"}>{vueCorrigee ? "Version corrigée (Pool)" : "Version compagnie (brute)"}</Tag>
                                                    <Switch checked={vueCorrigee} onChange={setVueCorrigee}
                                                        checkedChildren="Corrigée" unCheckedChildren="Compagnie" />
                                                    <Tooltip title="En vue « Compagnie », toutes les lignes sont affichées et vous pouvez les écarter. En vue « Corrigée », les lignes écartées sont retirées des totaux et de la rétrocession.">
                                                        <span style={{ fontSize: 12, color: "#6b7280" }}>ⓘ Écarter / Rétablir depuis la vue « Compagnie ».</span>
                                                    </Tooltip>
                                                </Space>
                                                <Space wrap>
                                                    {exclusions.length > 0 ? (
                                                        <>
                                                            <Badge count={exclusions.length} style={{ backgroundColor: "#fa8c16" }} />
                                                            <span style={{ fontSize: 13, color: "#ad4e00" }}>
                                                                ligne(s) écartée(s) · Montant écarté :{" "}
                                                                <b>{displayNumbers(Math.trunc(exclusions.reduce((s, e) => s + Math.abs(Number(e.montant) || 0), 0)))} F</b>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span style={{ fontSize: 13, color: "#6b7280" }}>Aucune ligne écartée — versions compagnie et corrigée identiques.</span>
                                                    )}
                                                </Space>
                                            </div>
                                            <div className="retro-tabs">
                                                <Tabs
                                                    onChange={onTabsChange}
                                                    activeKey={tabId}
                                                    items={[
                                                        {
                                                            label: (
                                                                <span>
                                                                    Émissions
                                                                    {validationState.emissions.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "1",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="emissions"
                                                                    columns={emissionsColumns(compagniesRepartition, false, exclusionOptsFor("EMISSION"))}
                                                                    activeKeys={activeEmissionsKeys}
                                                                    setActiveKeys={setActiveEmissionsKeys}
                                                                    processData={(compagnie) => {
                                                                        const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
                                                                        const data = filtrerCorrige("EMISSION", emissionsRetrocedees);
                                                                        return {
                                                                            data,
                                                                            totalOriginal: data.reduce((sum, e) => sum + (e.montantOriginal || 0), 0),
                                                                            totalRetrocede: data.reduce((sum, e) => sum + (e.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationEmissions}
                                                                    isValidated={validationState.emissions.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Annulations
                                                                    {validationState.annulations.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "2",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="annulations"
                                                                    columns={annulationsColumns(compagniesRepartition, false, exclusionOptsFor("ANNULATION"))}
                                                                    activeKeys={activeAnnulationsKeys}
                                                                    setActiveKeys={setActiveAnnulationsKeys}
                                                                    processData={(compagnie) => {
                                                                        const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
                                                                        const data = filtrerCorrige("ANNULATION", annulationsRetrocedees);
                                                                        return {
                                                                            data,
                                                                            totalOriginal: data.reduce((sum, a) => sum + (a.montantOriginal || 0), 0),
                                                                            totalRetrocede: data.reduce((sum, a) => sum + (a.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationAnnulations}
                                                                    isValidated={validationState.annulations.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Sinistres payés
                                                                    {validationState.sinistresPayes.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "3",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="sinistres"
                                                                    columns={sinistresColumns(compagniesRepartition, exclusionOptsFor("SINISTRE"))}
                                                                    activeKeys={activeSinistresKeys}
                                                                    setActiveKeys={setActiveSinistresKeys}
                                                                    processData={(compagnie) => {
                                                                        const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
                                                                        const data = filtrerCorrige("SINISTRE", sinistresRetrocedes);
                                                                        return {
                                                                            data,
                                                                            totalOriginal: data.reduce((sum, s) => sum + (s.montantOriginal || 0), 0),
                                                                            totalRetrocede: data.reduce((sum, s) => sum + (s.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationSinistres}
                                                                    isValidated={validationState.sinistresPayes.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Recours encaissés
                                                                    {validationState.recoursEncaisses.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "4",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="recours"
                                                                    columns={recoursColumns(compagniesRepartition, exclusionOptsFor("RECOURS"))}
                                                                    activeKeys={activeRecoursKeys}
                                                                    setActiveKeys={setActiveRecoursKeys}
                                                                    processData={(compagnie) => {
                                                                        const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);
                                                                        const data = filtrerCorrige("RECOURS", recoursRetrocedes);
                                                                        return {
                                                                            data,
                                                                            totalOriginal: data.reduce((sum, r) => sum + (r.montantOriginal || 0), 0),
                                                                            totalRetrocede: data.reduce((sum, r) => sum + (r.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationRecours}
                                                                    isValidated={validationState.recoursEncaisses.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: "📊 Récapitulatif",
                                                            key: "5",
                                                            children: (
                                                                <div className="recap-container">
                                                                    <div className="recap-grid">
                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#f6ffed', color: '#52c41a' }}>
                                                                                    <CheckCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Émissions</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#52c41a' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).totalEmissions)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fffbe6', color: '#faad14' }}>
                                                                                    <MinusCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Annulations</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#faad14' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).totalAnnulations)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fff2f0', color: '#ff4d4f' }}>
                                                                                    <WarningOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Sinistres</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#ff4d4f' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).totalSinistres)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#e6fffb', color: '#13c2c2' }}>
                                                                                    <DollarOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Recours</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#13c2c2' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).totalRecours)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fff1f0', color: '#cf1322' }}>
                                                                                    <ClockCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Pénalités</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#cf1322' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).totalPenalites)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card recap-total-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                                                                                    <BankOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Solde net</span>
                                                                            </div>
                                                                            <div className="recap-card-value">
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession, vueCorrigee ? exclusionIndex : null).solde)} F
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <Divider />

                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                                                                        <Button
                                                                            icon={<EyeOutlined />}
                                                                            size="large"
                                                                            onClick={previsualiserRecap}
                                                                            loading={loadingApercu}
                                                                        >
                                                                            Prévisualiser le récapitulatif
                                                                        </Button>
                                                                        <Button
                                                                            icon={<PrinterOutlined />}
                                                                            size="large"
                                                                        >
                                                                            Imprimer
                                                                        </Button>
                                                                        <Button
                                                                            type="primary"
                                                                            danger
                                                                            size="large"
                                                                            icon={<CheckOutlined />}
                                                                            onClick={confirmValidationRecapitulatif}
                                                                            disabled={isRetrocessionValidee}
                                                                            loading={loadingRetrocession}
                                                                        >
                                                                            Valider et clôturer
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </Skeleton>
                                    </ConfigProvider>
                                </div>
                            </div>
                            )}
                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>

            <MotifExclusionModal
                open={motifModal.open}
                context={motifModal.context}
                onClose={() => setMotifModal({ open: false, context: null })}
                onSaved={chargerExclusions}
            />

            {/* APERÇU du récapitulatif détaillé AVANT validation (données /preview, non persistées) */}
            <Modal
                open={apercuVisible}
                onCancel={() => setApercuVisible(false)}
                footer={null}
                width={1200}
                style={{ top: 20 }}
                title={`Aperçu du récapitulatif — ${selectedCategorie || ""} ${moisRetrocession || ""} (avant validation)`}
                destroyOnClose
            >
                {apercuGroup
                    ? <TableauRecapitulatifRetrocession selectedGroup={apercuGroup} />
                    : null}
            </Modal>
        </div>
    );
};

export default Retrocession;